import { DeviceResponseReportWrapper } from "src/repositories/api/dto/DeviceResponseReportWrapper";
import { DeviceResponseReport } from "src/services/core/device/models/DeviceResponseReport";
import ManagedUser from "src/services/core/managed-user/models/ManagedUser";
import {
  AddManagedUserDto,
  AddManagedUserOutput,
  PatchManagedUserDto,
  PatchManagedUserOutput,
  UpdateManagedUserDto,
} from "src/services/core/managed-user/models/ManagedUserDto";
import UploadFingerprintDto from "src/services/core/managed-user/models/UploadFingerprintDto";
import { isNumberArray, isStringArray } from "src/utils/Utils";
import {
  FailedResponse,
  createFailedResponse,
  createSuccessResponse,
} from "../model/FailableResponse";
import { globalAxiosAction } from "./Init.api";
import { createDefaultAxios } from "./Utils/AxiosUtility";
import {
  GeneralApiResponse,
  handleGeneralApiError,
} from "./Utils/ResponseUtility";
import { getBrearerTokenHeader } from "./Utils/Utils.api";

const axiosInstance = createDefaultAxios("/api/manageduser/");
const longRunningAxiosInstance = createDefaultAxios("/api/manageduser/", 0);
globalAxiosAction.subscribe(axiosInstance);

/**
 *
 * @param activeState parameter to filter the managedUser by based on the value of managedUser.isActive,
 * null means that both managedUser's with isActive == true and isActive == false are returned.
 * The default value is true.
 */
async function getManagedUsers(
  groupId: number,
  activeState: boolean | null = true
): Promise<GeneralApiResponse<ManagedUser[]>> {
  try {
    const response = await axiosInstance.get<ManagedUser[]>(
      "getManagedUsers/" + groupId,
      {
        ...getBrearerTokenHeader(),
        params: { activeState: activeState ?? "both" },
      }
    );
    return createSuccessResponse(response.data);
  } catch (e) {
    return handleGeneralApiError(e);
  }
}

async function deleteManagedUsers(
  managedUserIdList: number[],
  deviceSnList: string[]
): Promise<GeneralApiResponse<DeviceResponseReport | null>> {
  try {
    const response = await axiosInstance.delete<DeviceResponseReport | null>(
      "deleteManagedUsers",
      {
        params: {
          managedUserIdList,
          deviceSnList,
        },
        ...getBrearerTokenHeader(),
      }
    );
    return createSuccessResponse(response.data);
  } catch (e) {
    return handleGeneralApiError(e);
  }
}

// fingerprintSourceSn decide whether fingerprints of a managed user are uploaded from a device
async function addManagedUsers(
  groupId: number,
  managedUserList: AddManagedUserDto[],
  deviceSnList: string[],
  fingerprintSourceSn?: string
): Promise<
  | GeneralApiResponse<AddManagedUserOutput>
  | FailedResponse<"devices-not-connected", string[]>
  | FailedResponse<"id-conflict", number[]>
> {
  try {
    const response = await longRunningAxiosInstance.post<AddManagedUserOutput>(
      "addManagedUsers/" + groupId,
      {
        managedUsers: managedUserList,
        deviceSnList,
        forceRewriteUsersOnDevices: true,
      },
      {
        ...getBrearerTokenHeader(),
        params: { fingerprintSourceSn },
      }
    );
    return createSuccessResponse(response.data);
  } catch (e) {
    return handleGeneralApiError(e, (error, status, data) => {
      if (status == 404 && isStringArray(data.devicesNotConnected)) {
        return createFailedResponse(
          "devices-not-connected",
          data.devicesNotConnected
        );
      } else if (status == 409 && isNumberArray(data.conflictedIds)) {
        return createFailedResponse("id-conflict", data.conflictedIds);
      }
      return null;
    });
  }
}

// fingerprintSourceSn decide whether fingerprints of a managed user are uploaded from a device
async function patchManagedUser(
  managedUserDto: PatchManagedUserDto,
  deviceSnList: string[],
  fingerprintSourceSn?: string
): Promise<
  | GeneralApiResponse<PatchManagedUserOutput>
  | FailedResponse<"id-conflict", number[]>
> {
  try {
    const response = await longRunningAxiosInstance.patch<PatchManagedUserOutput>(
      "patchManagedUser",
      {
        managedUser: managedUserDto,
        deviceSnList,
        fingerprintSourceSn,
      },
      {
        ...getBrearerTokenHeader(),
        params: { fingerprintSourceSn },
      }
    );
    return createSuccessResponse(response.data);
  } catch (e) {
    return handleGeneralApiError(e, (error, status, data) => {
      if (status == 409 && isNumberArray(data.conflictedIds)) {
        return createFailedResponse("id-conflict", data.conflictedIds);
      }
      return null;
    });
  }
}

// fingerprintSourceSn decide whether fingerprints of managed users are uploaded from a device
async function addUpdateManagedUser(
  groupId: number,
  added: AddManagedUserDto[],
  updated: UpdateManagedUserDto[],
  fingerprintSourceSn?: string,
  uploadFingerprintTargets?: UploadFingerprintDto[]
): Promise<GeneralApiResponse<DeviceResponseReportWrapper | undefined>> {
  try {
    const response = await longRunningAxiosInstance.post(
      `addUpdateManagedUsers/${groupId}`,
      {
        added,
        updated,
        uploadFingerprintTargets
      },
      {
        ...getBrearerTokenHeader(),
        params: { fingerprintSourceSn },
      }
    );
    return createSuccessResponse(response.data);
  } catch (e) {
    return handleGeneralApiError(e);
  }
}

export const managedUserApi = {
  getManagedUsers,
  deleteManagedUsers,
  patchManagedUser,
  addManagedUsers,
  addUpdateManagedUser,
};
